import { isAfter, isBefore } from 'date-fns';
import { Habit } from 'shared/types/habit';
import { RepeatingTask } from 'shared/types/repeating-task';
import { Task, UpdateTask } from 'shared/types/task';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import {
  createPlannedOccurrenceForDate,
  deletePlannedOccurrenceForDate,
} from 'shared/utils/schedules';

export type Options = {
  weekStartsOn: WeekDays;
  today: Timestamp;
};

export const buildDateUpdateTasks = (
  tasks: Task[],
  date: Timestamp | undefined,
  currentValue: Timestamp | undefined,
  { weekStartsOn, today }: Options,
): UpdateTask[] => {
  const tasksToUpdate: UpdateTask[] = [];

  for (const task of tasks) {
    if (task.schedules?.length) {
      const scheduledTask = task as Habit | RepeatingTask;

      if (date) {
        const schedules = createPlannedOccurrenceForDate(
          scheduledTask.schedules,
          date,
          {
            weekStartsOn,
            today,
          },
        );
        tasksToUpdate.push({
          id: task.id,
          schedules,
        });
        continue;
      }

      if (!currentValue) {
        continue;
      }

      const schedules = scheduledTask.schedules
        ?.toSorted(
          (scheduleA, scheduleB) =>
            scheduleA.startDate.getTime() - scheduleB.startDate.getTime(),
        )
        .map((_schedule, index, arr) => {
          const prevScheduleEndDate = arr[index - 1]?.endDate;
          // if the date is not part of the schedule that needs to be updated, the schedule is good as is
          if (
            (prevScheduleEndDate &&
              isBefore(currentValue, prevScheduleEndDate)) ||
            (_schedule.endDate && isAfter(currentValue, _schedule.endDate))
          ) {
            return _schedule;
          }

          return deletePlannedOccurrenceForDate(_schedule, currentValue, {
            weekStartsOn,
            today,
          });
        });

      tasksToUpdate.push({
        id: task.id,
        schedules,
      });
      continue;
    }

    tasksToUpdate.push({
      id: task.id,
      endStrategy: {
        deadline: date ?? null,
        completionCount: task.endStrategy?.completionCount ?? null,
      },
    });
  }

  return tasksToUpdate;
};
